import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DASHBOARD_PATHS } from "@/lib/paths";
import { api } from '@/lib/api';
import { bpPrompt } from '@/lib/dialogs';

// ---------------------------------------------------------------------------
// Bug Report Widget — region screenshot OR screen-recording → annotate →
// submit. Server uploads media to S3 and creates a GitHub Issue with the
// screenshot inlined and a link to the video.
// ---------------------------------------------------------------------------

type Mode = 'screenshot' | 'video';
type Step = 'pick' | 'region' | 'annotate' | 'recording' | 'submit';
type Tool = 'pen' | 'arrow' | 'rect' | 'text';
type Color = '#ef4444' | '#f59e0b' | '#22c55e' | '#3b82f6';

interface Annotation {
  tool: Tool;
  color: Color;
  points?: Array<[number, number]>;
  start?: [number, number];
  end?: [number, number];
  text?: string;
}

interface Region { x: number; y: number; w: number; h: number }

const VIDEO_MAX_SECONDS = 60;

export function BugReportWidget() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('screenshot');
  const [step, setStep] = useState<Step>('pick');

  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [currentTool, setCurrentTool] = useState<Tool>('pen');
  const [currentColor, setCurrentColor] = useState<Color>('#ef4444');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consoleErrors, setConsoleErrors] = useState<string[]>([]);

  // Region-select transient state
  const [regionDraft, setRegionDraft] = useState<Region | null>(null);
  const [capturing, setCapturing] = useState(false);
  const regionStartRef = useRef<[number, number] | null>(null);
  const screenshotStreamRef = useRef<MediaStream | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const currentAnnotationRef = useRef<Annotation | null>(null);

  // Video machinery
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // ── Console error capture (always on, even before the user opens the widget) ──
  useEffect(() => {
    const errors: string[] = [];
    const originalError = console.error;
    console.error = (...args: unknown[]) => {
      errors.push(args.map(a => typeof a === 'string' ? a : JSON.stringify(a)).join(' '));
      if (errors.length > 20) errors.shift();
      originalError.apply(console, args);
    };
    setConsoleErrors(errors);
    return () => { console.error = originalError; };
  }, []);

  // ── Cleanup on unmount: stop any in-flight recording, revoke blob URLs ──
  useEffect(() => {
    return () => {
      const r = mediaRecorderRef.current;
      if (r && r.state === 'recording') r.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
      screenshotStreamRef.current?.getTracks().forEach(t => t.stop());
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);
  useEffect(() => {
    return () => { if (videoUrl) URL.revokeObjectURL(videoUrl); };
  }, [videoUrl]);

  const reset = useCallback(() => {
    setStep('pick');
    setScreenshot(null);
    setAnnotations([]);
    setDescription('');
    setRecordingSeconds(0);
    setRegionDraft(null);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
    setVideoBlob(null);
  }, [videoUrl]);

  // The launcher lives in the sidebar footer (next to the build/commit line) and
  // fires this event.
  useEffect(() => {
    const onOpen = () => { reset(); setOpen(true); };
    window.addEventListener('bp:open-bug-report', onOpen);
    return () => window.removeEventListener('bp:open-bug-report', onOpen);
  }, [reset]);

  const closeAll = () => {
    const r = mediaRecorderRef.current;
    if (r && r.state === 'recording') r.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    screenshotStreamRef.current?.getTracks().forEach(t => t.stop());
    screenshotStreamRef.current = null;
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setOpen(false);
    reset();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Region screenshot
  // ─────────────────────────────────────────────────────────────────────────

  const beginRegionSelect = async () => {
    setMode('screenshot');
    setRegionDraft(null);
    try {
      // Native screen capture — instant on mouseup vs ~4s for html-to-image
      // on a busy dashboard. preferCurrentTab + selfBrowserSurface trims
      // Chrome's picker to a single Share confirmation.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30 } },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      } as DisplayMediaStreamOptions & { preferCurrentTab?: boolean; selfBrowserSurface?: string });
      screenshotStreamRef.current = stream;
    } catch {
      toast.error('Screen capture permission denied');
      return;
    }
    setOpen(false);   // hide the modal so the page is visible underneath
    setStep('region');
  };

  const handleRegionMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    regionStartRef.current = [e.clientX, e.clientY];
    setRegionDraft({ x: e.clientX, y: e.clientY, w: 0, h: 0 });
  };

  const handleRegionMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!regionStartRef.current) return;
    const [sx, sy] = regionStartRef.current;
    const x = Math.min(sx, e.clientX);
    const y = Math.min(sy, e.clientY);
    const w = Math.abs(e.clientX - sx);
    const h = Math.abs(e.clientY - sy);
    setRegionDraft({ x, y, w, h });
  };

  const handleRegionMouseUp = async () => {
    const region = regionDraft;
    regionStartRef.current = null;
    if (!region || region.w < 10 || region.h < 10) {
      // Too small — treat as misclick, stay in region mode
      setRegionDraft(null);
      return;
    }
    setRegionDraft(null);
    // Hide the overlay (dim + hint banner) before snapshotting — tab capture
    // sees live DOM so the overlay would be in the frame. Two RAFs to let
    // React flush the unmount + the browser commit a paint.
    setCapturing(true);
    await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    try {
      await captureRegion(region);
    } finally {
      setCapturing(false);
    }
  };

  const cancelRegionSelect = () => {
    regionStartRef.current = null;
    setRegionDraft(null);
    screenshotStreamRef.current?.getTracks().forEach(t => t.stop());
    screenshotStreamRef.current = null;
    setStep('pick');
    setOpen(true);
  };

  // Listen for Escape during region select
  useEffect(() => {
    if (step !== 'region') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelRegionSelect(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step]);

  const captureRegion = useCallback(async (region: Region) => {
    const stream = screenshotStreamRef.current;
    if (!stream) {
      toast.error('Screen capture stream not ready');
      setStep('pick');
      setOpen(true);
      return;
    }
    try {
      // Grab one frame from the live screen-capture track. Much faster
      // than html-to-image rasterizing the whole DOM.
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      // Wait one frame so videoWidth/Height are populated.
      await new Promise<void>(r => requestAnimationFrame(() => r()));

      // Capture surface (tab/window/screen) may not match viewport
      // dimensions — scale region coords accordingly.
      const scaleX = video.videoWidth / window.innerWidth;
      const scaleY = video.videoHeight / window.innerHeight;
      const sx = region.x * scaleX;
      const sy = region.y * scaleY;
      const sw = region.w * scaleX;
      const sh = region.h * scaleY;

      const cropped = document.createElement('canvas');
      cropped.width = sw;
      cropped.height = sh;
      const ctx = cropped.getContext('2d')!;
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);

      setScreenshot(cropped.toDataURL('image/png'));
      setStep('annotate');
      setOpen(true);
    } catch (err) {
      console.error('Bug widget: screenshot failed', err);
      const msg = err instanceof Error ? err.message : '';
      toast.error(`Failed to capture screenshot${msg ? `: ${msg}` : ''}`);
      setStep('pick');
      setOpen(true);
    } finally {
      stream.getTracks().forEach(t => t.stop());
      screenshotStreamRef.current = null;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Video recording (getDisplayMedia + MediaRecorder)
  // ─────────────────────────────────────────────────────────────────────────

  const beginVideoRecording = useCallback(async () => {
    setMode('video');
    setOpen(false); // browser shows its own native picker; modal would just be in the way
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 24 } },
        audio: false,
      });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(recordingChunksRef.current, { type: 'video/webm' });
        setVideoBlob(blob);
        setVideoUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        setStep('submit');
        setOpen(true);
      };

      // Browser's native "Stop sharing" pill ends the track; finalize cleanly.
      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state === 'recording') recorder.stop();
      };

      recorder.start();
      setRecordingSeconds(0);
      setStep('recording');

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(s => {
          const next = s + 1;
          if (next >= VIDEO_MAX_SECONDS && recorder.state === 'recording') {
            recorder.stop();
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      // User denied permission, or browser doesn't support getDisplayMedia
      const msg = (err instanceof Error && err.name === 'NotAllowedError')
        ? 'Screen recording permission denied'
        : 'Screen recording not supported in this browser';
      toast.error(msg);
      setStep('pick');
      setOpen(true);
    }
  }, []);

  const stopVideoRecording = () => {
    const r = mediaRecorderRef.current;
    if (r && r.state === 'recording') r.stop();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Annotation drawing on canvas
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!canvasRef.current || !screenshot) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      for (const ann of annotations) {
        ctx.strokeStyle = ann.color;
        ctx.fillStyle = ann.color;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';

        if (ann.tool === 'pen' && ann.points && ann.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(ann.points[0][0], ann.points[0][1]);
          for (let i = 1; i < ann.points.length; i++) {
            ctx.lineTo(ann.points[i][0], ann.points[i][1]);
          }
          ctx.stroke();
        } else if (ann.tool === 'arrow' && ann.start && ann.end) {
          const [sx, sy] = ann.start;
          const [ex, ey] = ann.end;
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(ex, ey);
          ctx.stroke();
          const angle = Math.atan2(ey - sy, ex - sx);
          ctx.beginPath();
          ctx.moveTo(ex, ey);
          ctx.lineTo(ex - 15 * Math.cos(angle - 0.4), ey - 15 * Math.sin(angle - 0.4));
          ctx.moveTo(ex, ey);
          ctx.lineTo(ex - 15 * Math.cos(angle + 0.4), ey - 15 * Math.sin(angle + 0.4));
          ctx.stroke();
        } else if (ann.tool === 'rect' && ann.start && ann.end) {
          const [sx, sy] = ann.start;
          const [ex, ey] = ann.end;
          ctx.strokeRect(sx, sy, ex - sx, ey - sy);
        } else if (ann.tool === 'text' && ann.start && ann.text) {
          ctx.font = 'bold 16px sans-serif';
          ctx.fillText(ann.text, ann.start[0], ann.start[1]);
        }
      }
    };
    img.src = screenshot;
  }, [screenshot, annotations, step]);

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>): [number, number] => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return [(e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY];
  };

  const handleMouseDown = async (e: React.MouseEvent<HTMLCanvasElement>) => {
    const [x, y] = getCanvasCoords(e);
    drawingRef.current = true;

    if (currentTool === 'text') {
      const text = await bpPrompt('Enter text:');
      if (text) {
        setAnnotations(prev => [...prev, { tool: 'text', color: currentColor, start: [x, y], text }]);
      }
      drawingRef.current = false;
      return;
    }

    currentAnnotationRef.current = {
      tool: currentTool,
      color: currentColor,
      points: currentTool === 'pen' ? [[x, y]] : undefined,
      start: currentTool !== 'pen' ? [x, y] : undefined,
    };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || !currentAnnotationRef.current) return;
    const [x, y] = getCanvasCoords(e);
    const ann = currentAnnotationRef.current;

    if (ann.tool === 'pen') {
      ann.points!.push([x, y]);
      setAnnotations(prev => [...prev.slice(0, -1), { ...ann, points: [...ann.points!] }]);
      if (annotations.length === 0 || annotations[annotations.length - 1] !== ann) {
        setAnnotations(prev => [...prev, { ...ann }]);
      }
    } else {
      ann.end = [x, y];
      const existing = annotations.filter(a => a !== currentAnnotationRef.current);
      setAnnotations([...existing, { ...ann }]);
    }
  };

  const handleMouseUp = () => {
    drawingRef.current = false;
    currentAnnotationRef.current = null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!description.trim()) { toast.error('Please describe the bug'); return; }
    setSubmitting(true);

    try {
      const fd = new FormData();
      fd.append('description', description.trim());
      fd.append('url', window.location.href);
      fd.append('userAgent', navigator.userAgent);
      fd.append('viewport', `${window.innerWidth}x${window.innerHeight}`);
      fd.append('consoleErrors', JSON.stringify(consoleErrors.slice(-10)));
      fd.append('timestamp', new Date().toISOString());

      if (mode === 'screenshot') {
        const canvas = canvasRef.current;
        const dataUrl = canvas?.toDataURL('image/png') ?? screenshot ?? '';
        if (dataUrl) {
          // Decode the data URL inline instead of fetch()ing it — CSP
          // `connect-src` (intentionally restricted to `self`/https/ws/wss)
          // refuses the data: scheme and the upload would be blocked.
          const comma = dataUrl.indexOf(',');
          const meta  = dataUrl.slice(0, comma);
          const b64   = dataUrl.slice(comma + 1);
          const mime  = /:(.*?);/.exec(meta)?.[1] ?? 'image/png';
          const bin   = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          fd.append('screenshot', new Blob([bytes], { type: mime }), 'screenshot.png');
        }
      } else if (mode === 'video' && videoBlob) {
        fd.append('video', videoBlob, 'video.webm');
      }

      const res = await api.raw<{ issueUrl?: string; dedup?: boolean; error?: string }>(
        DASHBOARD_PATHS.bugReport,
        { method: 'POST', body: fd, ignoreResponseError: true },
      );

      if (res.ok) {
        const data = res._data ?? {};
        if (data.issueUrl) {
          const msg = data.dedup ? 'Reproduced existing GitHub Issue' : 'Bug reported! GitHub Issue created';
          toast.success(msg, {
            action: { label: 'View issue', onClick: () => window.open(data.issueUrl, '_blank', 'noopener') },
            duration: 8000,
          });
        } else {
          toast.success('Bug reported (GitHub not configured — admins can wire it in Settings → GitHub)');
        }
        closeAll();
      } else {
        toast.error(res._data?.error || 'Failed to submit bug report');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  // Region select overlay: rendered when step === 'region', regardless of `open`.
  // Hidden during the actual snapshot so its own pixels don't end up in the PNG.
  // No dim while idle — the page must stay visible so the user can see the bug
  // they're trying to capture (dark dashboards turn pitch-black under a 30% dim).
  const regionOverlay = step === 'region' && !capturing ? (
    <div
      className="fixed inset-0 z-[10000] cursor-crosshair select-none"
      style={{ backgroundColor: 'transparent' }}
      onMouseDown={handleRegionMouseDown}
      onMouseMove={handleRegionMouseMove}
      onMouseUp={handleRegionMouseUp}
    >
      {regionDraft && (
        <div
          className="absolute border-2 border-white pointer-events-none"
          style={{
            left: regionDraft.x,
            top: regionDraft.y,
            width: regionDraft.w,
            height: regionDraft.h,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
          }}
        />
      )}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs px-3 py-1.5 rounded-md pointer-events-none">
        Drag to select bug region · Press Esc to cancel
      </div>
    </div>
  ) : null;

  // Floating "stop recording" pill while recording, in case the user wants to
  // end early without using the browser's native sharing controls.
  const recordingPill = step === 'recording' ? (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] bg-destructive text-destructive-foreground rounded-full shadow-lg px-4 py-2 flex items-center gap-3">
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
      </span>
      <span className="text-sm font-medium tabular-nums">
        Recording {recordingSeconds}s / {VIDEO_MAX_SECONDS}s
      </span>
      <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={stopVideoRecording}>
        Stop
      </Button>
    </div>
  ) : null;

  if (!open) {
    // No floating FAB — the trigger is a small button in the sidebar footer
    // (AppLayout), which dispatches `bp:open-bug-report`. We still render the
    // region overlay + recording pill here for the in-progress capture states.
    return (
      <>
        {regionOverlay}
        {recordingPill}
      </>
    );
  }

  return (
    <>
      {regionOverlay}
      {recordingPill}
      <div id="bug-report-modal" className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="w-full max-w-3xl mx-4 rounded-xl bg-background border border-border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
            <h2 className="text-sm font-semibold">Report a Bug</h2>
            <div className="flex items-center gap-2">
              {step === 'annotate' && (
                <>
                  <span className="text-xs text-muted-foreground mr-2">Draw on the screenshot to highlight the issue</span>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setStep('submit')}>
                    Next
                  </Button>
                </>
              )}
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={closeAll}>&times;</Button>
            </div>
          </div>

          {/* Step: pick capture mode */}
          {step === 'pick' && (
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                What kind of bug do you want to report?
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={beginRegionSelect}
                  className="rounded-lg border border-border bg-card hover:border-primary hover:bg-accent/30 p-4 text-left transition-colors"
                >
                  <div className="text-sm font-semibold mb-1">Screenshot a region</div>
                  <div className="text-xs text-muted-foreground">
                    Drag a rectangle around the issue, then annotate it.
                  </div>
                </button>
                <button
                  onClick={beginVideoRecording}
                  className="rounded-lg border border-border bg-card hover:border-primary hover:bg-accent/30 p-4 text-left transition-colors"
                >
                  <div className="text-sm font-semibold mb-1">Record a video</div>
                  <div className="text-xs text-muted-foreground">
                    Record up to {VIDEO_MAX_SECONDS}s of your screen to show the bug in action.
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step: annotate (screenshot mode only) */}
          {step === 'annotate' && screenshot && (
            <>
              <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/30">
                {([['pen', '✎'], ['arrow', '↗'], ['rect', '□'], ['text', 'T']] as [Tool, string][]).map(([t, icon]) => (
                  <button
                    key={t}
                    onClick={() => setCurrentTool(t)}
                    className={cn('w-8 h-8 rounded flex items-center justify-center text-sm', currentTool === t ? 'bg-primary text-primary-foreground' : 'hover:bg-accent')}
                  >
                    {icon}
                  </button>
                ))}
                <div className="w-px h-6 bg-border mx-1" />
                {(['#ef4444', '#f59e0b', '#22c55e', '#3b82f6'] as Color[]).map(c => (
                  <button
                    key={c}
                    onClick={() => setCurrentColor(c)}
                    className={cn('w-6 h-6 rounded-full border-2', currentColor === c ? 'border-foreground' : 'border-transparent')}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <div className="flex-1" />
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAnnotations([])}>Clear</Button>
              </div>

              <div className="flex-1 overflow-auto p-2 bg-zinc-900">
                <canvas
                  ref={canvasRef}
                  className="max-w-full cursor-crosshair"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
              </div>
            </>
          )}

          {/* Step: submit (both modes converge here) */}
          {step === 'submit' && (
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {/* Media preview */}
              {mode === 'screenshot' && screenshot && (
                <div className="relative">
                  <canvas ref={canvasRef} className="w-full rounded border border-border" />
                  <Button variant="outline" size="sm" className="absolute top-2 right-2 h-6 text-xs" onClick={() => setStep('annotate')}>
                    Edit
                  </Button>
                </div>
              )}
              {mode === 'video' && videoUrl && (
                <video src={videoUrl} controls playsInline className="w-full rounded border border-border max-h-[40vh]" />
              )}

              <div>
                <label className="text-sm font-medium">What happened?</label>
                <textarea
                  className="w-full mt-1 min-h-[80px] rounded-md border border-border bg-card text-foreground p-3 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-ring"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe the bug... What did you expect to happen?"
                  autoFocus
                />
              </div>

              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">Technical details (auto-captured)</summary>
                <div className="mt-2 space-y-1 font-mono bg-muted rounded p-2">
                  <div>URL: {window.location.href}</div>
                  <div>Viewport: {window.innerWidth}x{window.innerHeight}</div>
                  <div>Browser: {navigator.userAgent.slice(0, 80)}...</div>
                  {consoleErrors.length > 0 && (
                    <div className="text-red-500">Console errors: {consoleErrors.length}</div>
                  )}
                </div>
              </details>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={closeAll}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={submitting || !description.trim()}>
                  {submitting ? 'Submitting...' : 'Submit Bug Report'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
